import { type ActionContext } from 'vuex';

import { patchSeonSession } from '@/api/insertions';
import { SourcePlatform } from '@/types';
import seon from '@seontechnologies/seon-javascript-sdk';
import { captureError } from '@smg-real-estate/browser-rum';

export interface SeonState {
  seonSession: string | null;
  sessionId: string | null;
}

export default {
  namespaced: true,
  state: { seonSession: null },
  getters: { getSeonSession },
  mutations: { setSeonSession },
  actions: { configure, clear, saveSeonSession },
};

function getSeonSession(state: SeonState): string | null {
  return state.seonSession;
}

function setSeonSession(state: SeonState, seonSession: string): void {
  state.seonSession = seonSession;
}

async function configure(
  { commit }: ActionContext<SeonState, any>,
  payload: { insertionId: string; userId: string },
): Promise<void> {
  const { insertionId, userId } = payload;
  if (!insertionId || !userId) {
    captureError(new Error('Missing required parameter: insertionId or userId'), {
      service: 'seon',
      action: 'configure',
    });
    return;
  }

  try {
    // configure the SEON plugin
    seon.init();
    const config = {
      geolocation: {
        canPrompt: false,
      },
      networkTimeoutMs: 2000,
      fieldTimeoutMs: 2000,
      region: 'eu',
      silentMode: true,
    };

    // generate the SEON fingerprint and store it in the store
    const fingerprint = await seon.getSession(config);
    commit('setSeonSession', fingerprint);
    console.log('SEON Session initialized successfully');
  } catch (e) {
    captureError(e, { service: 'seon', action: 'configure' });
    if (e.name === 'SEONConfigError') {
      console.error('SEON Config Error:', e.message);
    } else {
      console.error('SEON Unknown Error:', e);
    }
  }
}

async function saveSeonSession(
  context: ActionContext<SeonState, any>,
  {
    ownerId,
    insertionId,
    userEnrolledInMFA,
    requiredMFA,
    mfaEnrollmentDateUtc,
  }: {
    ownerId: string;
    insertionId: string;
    userEnrolledInMFA: boolean;
    requiredMFA: boolean;
    mfaEnrollmentDateUtc?: string;
  },
): Promise<void> {
  try {
    if (context.state.seonSession) {
      await patchSeonSession(ownerId, insertionId, SourcePlatform.SMG, context.state.seonSession, {
        userEnrolledInMFA,
        requiredMFA,
        mfaEnrollmentDateUtc,
      });
    }
  } catch (error) {
    captureError(error, { service: 'seon', action: 'save', insertionId });
  }
}

function clear(context: ActionContext<SeonState, any>): void {
  context.commit('setSeonSession', null);
}
